import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  ArrowLeft,
  User,
  Shield,
  Bell,
  Palette
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50/50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </button>

          <div className="flex items-center space-x-3">
            <div className="p-3 bg-purple-100 rounded-lg text-purple-600">
              <Settings className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Settings</h1>
              <p className="text-gray-600 mt-0.5">Configure your workspace preferences and integrations</p>
            </div>
          </div>
        </div>

        {/* Profile Settings Panel */}
        <div className="bg-white rounded-xl shadow-sm border p-8 animate-fadeIn">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
              <User className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Profile Settings</h2>
              <p className="text-sm text-gray-500">Update your account information</p>
            </div>
          </div>

          <div className="space-y-6 max-w-xl">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name</label>
              <input
                type="text"
                disabled
                value={user?.name || ''}
                className="w-full bg-gray-50 border border-gray-300 rounded-lg py-2.5 px-3.5 text-sm text-gray-700 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
              <input
                type="email"
                disabled
                value={user?.email || ''}
                className="w-full bg-gray-50 border border-gray-300 rounded-lg py-2.5 px-3.5 text-sm text-gray-700 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Account Role</label>
              <input
                type="text"
                disabled
                value={user?.role === 'admin' ? 'Workspace Admin' : 'Workspace Member'}
                className="w-full bg-gray-50 border border-gray-300 rounded-lg py-2.5 px-3.5 text-sm text-gray-700 focus:outline-none"
              />
            </div>
          </div>

          {/* Showcase other settings coming soon */}
          <div className="mt-12 pt-8 border-t">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-6">Other Settings Panels</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="border rounded-lg p-4 bg-gray-50/50 flex flex-col justify-between">
                <div>
                  <Shield className="h-5 w-5 text-gray-400 mb-2" />
                  <h4 className="font-semibold text-sm text-gray-900">Security & Privacy</h4>
                  <p className="text-xs text-gray-500 mt-1">Passwords, session keys, and 2FA settings.</p>
                </div>
                <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full mt-3 self-start">Coming Soon</span>
              </div>
              <div className="border rounded-lg p-4 bg-gray-50/50 flex flex-col justify-between">
                <div>
                  <Bell className="h-5 w-5 text-gray-400 mb-2" />
                  <h4 className="font-semibold text-sm text-gray-900">Notifications</h4>
                  <p className="text-xs text-gray-500 mt-1">Configure email alerts and proctoring notifications.</p>
                </div>
                <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full mt-3 self-start">Coming Soon</span>
              </div>
              <div className="border rounded-lg p-4 bg-gray-50/50 flex flex-col justify-between">
                <div>
                  <Palette className="h-5 w-5 text-gray-400 mb-2" />
                  <h4 className="font-semibold text-sm text-gray-900">Branding</h4>
                  <p className="text-xs text-gray-500 mt-1">White-label options, colors, logos, and layouts.</p>
                </div>
                <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full mt-3 self-start">Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
